import { z } from "zod";
import type { Axios, AxiosResponse } from "axios";

import { packNetAxios } from "@/lib/api-client";
import { baseRoutes } from "@/api/base-routes";
import {
	type DesignFavorite,
	type GenerateSvgRequest,
	type GenerateSvgResponse,
	generateSvgResponseSchema,
	type PackagingDesign,
	packagingDesignSchema,
	type XmlUploadRequest,
} from "@/schemas/packaging-design";

export async function getPackagingDesigns(client: Axios = packNetAxios): Promise<PackagingDesign[]> {
	const response = await client.get<Array<PackagingDesign>>(baseRoutes.packagingDesign + "/v1.0/packagingdesigns");

	z.array(packagingDesignSchema).enrich({ callSite: "getPackagingDesigns" }).safeParseWithTelemetry(response.data);

	return response.data as PackagingDesign[];
}

export async function postDesign(design: PackagingDesign): Promise<PackagingDesign> {
	const response = await packNetAxios.post<PackagingDesign>(
		`${baseRoutes.packagingDesign}/v1.0/packagingdesigns`,
		design,
	);

	packagingDesignSchema.enrich({ callSite: "postDesign" }).safeParseWithTelemetry(response.data);

	return response.data as PackagingDesign;
}

export const uploadDesignXml = async ({
	ezdDesign,
	replace = false,
}: {
	ezdDesign: XmlUploadRequest;
	replace: boolean;
}) => {
	const response = await packNetAxios.post<PackagingDesign>(
		`${baseRoutes.packagingDesign}/v1.0/LegacyPackagingDesign/fromXmlString`,
		ezdDesign,
		{
			params: {
				replace,
			},
		},
	);

	packagingDesignSchema.enrich({ callSite: "uploadDesignXml" }).safeParseWithTelemetry(response.data);

	return response;
};

export async function updateEditedDesign({
	design,
	client = packNetAxios,
}: {
	design: PackagingDesign;
	client?: Axios;
}): Promise<PackagingDesign> {
	const response = await client.put<PackagingDesign>(
		`${baseRoutes.packagingDesign}/v1.0/packagingdesigns/${design.id}`,
		design,
	);

	packagingDesignSchema.enrich({ callSite: "updateEditedDesign" }).safeParseWithTelemetry(response.data);

	return response.data as PackagingDesign;
}

export async function deleteDesign(id: string, client: Axios = packNetAxios) {
	return await client.delete<void>(`${baseRoutes.packagingDesign}/v1.0/packagingdesigns/${id}`);
}

export const getDesignContents = async (designIds: number[]) =>
	await packNetAxios.post<ArrayBuffer>(
		`${baseRoutes.packagingDesign}/v1.0/packagingdesigns/content`,
		{ designIds },
		{ responseType: "arraybuffer" },
	);

export const toggleFavoriteDesign = (design: DesignFavorite) =>
	packNetAxios.post<void>(`${baseRoutes.packagingDesign}/v1.0/favorites`, {
		designId: design.designId,
		isFavorite: !design.isFavorite,
	});

export const getFavoriteDesigns = async (client: Axios): Promise<number[]> => {
	const response = await client.get<number[]>(`${baseRoutes.packagingDesign}/v1.0/favorites`);

	z.array(z.number()).enrich({ callSite: "getFavoriteDesigns" }).safeParseWithTelemetry(response.data);

	return response.data as number[];
};

export const generateCartonDesignPreview = async (
	payload: GenerateSvgRequest,
): Promise<AxiosResponse<GenerateSvgResponse>> => {
	const response = await packNetAxios.get<GenerateSvgResponse>(
		`${baseRoutes.packagingDesign}/v1/packagingDesigns/${payload.designId}/preview`,
		{
			params: {
				length: payload.length,
				width: payload.width,
				height: payload.height,
				rotation: payload.rotation,
				thickness: payload.thickness,
				...Object.entries(payload.xValues).reduce<Record<string, number>>((acc, [key, value]) => {
					acc[`xValue[${key}]`] = value;
					return acc;
				}, {}),
			},
		},
	);

	generateSvgResponseSchema.enrich({ callSite: "generateCartonDesignPreview" }).safeParseWithTelemetry(response.data);

	return response;
};

export const getCloudDesigns = async (): Promise<PackagingDesign[]> => {
	const response = await packNetAxios.get<Array<PackagingDesign>>(baseRoutes.packagingDesign + "/v1.0/Cloud");

	z.array(packagingDesignSchema).enrich({ callSite: "getCloudDesigns" }).safeParseWithTelemetry(response.data);

	return response.data as PackagingDesign[];
};

export async function getVersion(): Promise<string> {
	const response = await packNetAxios.get<string>(`${baseRoutes.packagingDesign}/v1/version`);

	return response.data;
}
